// Interface for location details
interface LocationDetails {
  locusV2Id: string;
  locusV2Type: string;
  country: string;
}

// Interface for date time details
interface DateTimeDetails {
  ts: number;
  zone: string;
}

// Interface for search context input parameters
interface SearchContextParams {
  countryCode?: string;
  fromDateTime?: DateTimeDetails;
  toDateTime?: DateTimeDetails;
  count?: number;
  profile?: string;
}

// Interface for the complete search context object
interface SearchContext {
  to?: {
    locusV2Id: string;
    locusV2Type: string;
    countryCode: string;
    locus: {
      country: string;
    };
  };
  fromDateTime?: DateTimeDetails;
  toDateTime?: DateTimeDetails;
  profile?: string;
  pax?: {
    count: number;
  }[];
}

/**
 * Extracts search context data from URL query parameters
 */
export const getSearchContextFromURL = (): SearchContextParams => {
  // Check if we're on the client side
  if (typeof window === "undefined") {
    return {};
  }

  const queryParams = new URLSearchParams(window.location.search);

  // Extract countries - take the first country as the primary destination
  const countries = queryParams.get("countries");
  const primaryCountry = countries ? countries.split(",")[0] : undefined;

  // Extract dates (they come as Unix timestamps in string format)
  const startDateStr = queryParams.get("startDate");
  const endDateStr = queryParams.get("endDate");
  const travellers = queryParams.get("travellers");

  // Build the search context parameters
  const searchContextParams: SearchContextParams = {};

  // Set destination info if available
  if (primaryCountry) {
    searchContextParams.countryCode = primaryCountry;
  }

  if (travellers) {
    searchContextParams.count = parseInt(travellers);
  }

  // Set date info if available
  if (startDateStr) {
    searchContextParams.fromDateTime = {
      ts: parseInt(startDateStr), // Convert to milliseconds if needed
      zone: "",
    };
  }

  searchContextParams.profile = "PERSONAL";

  if (endDateStr) {
    searchContextParams.toDateTime = {
      ts: parseInt(endDateStr), // Convert to milliseconds if needed
      zone: "",
    };
  }

  return searchContextParams;
};

/**
 * Creates a search context object based on the provided parameters
 */
export const createSearchContext = (
  params: SearchContextParams = {}
): SearchContext => {
  const searchContext: SearchContext = {};

  // Add destination details if provided
  if (params.countryCode) {
    searchContext.to = {
      locusV2Id: "",
      locusV2Type: "",
      countryCode: params.countryCode || "",
      locus: {
        country: params.countryCode || "",
      },
    };
  }

  // Add date time details if provided
  if (params.fromDateTime) {
    searchContext.fromDateTime = params.fromDateTime;
  }

  if (params.toDateTime) {
    searchContext.toDateTime = params.toDateTime;
  }

  // Add profile if provided
  if (params.profile) {
    searchContext.profile = params.profile;
  }

  if (params.count) {
    searchContext.pax = [
      {
        count: params.count,
      },
    ];
  }

  return searchContext;
};

/**
 * Generates the complete chat configuration with auto-extracted URL params
 */
export const generateChatConfigFromURL = (
  pageName: string,
  prevPageName?: string | null
) => {
  const searchContextParams = getSearchContextFromURL();
  const searchContext = createSearchContext(searchContextParams);

  return {
    context: {
      lob: "INSURANCE",
      lobCategory: "INSURANCE",
      view: pageName,
      project: "insurance_chatbot",
      prevPage: prevPageName,
    },
    contextMetadata: {
      pageContext: {
        lob: "INSURANCE",
        lobCategory: "INSURANCE",
        pageName: pageName,
        prevPageName: prevPageName,
      },
      searchContext: searchContext,
    },
    expertMetadata: {},
  };
};

// Type exports for external usage
export type {
  LocationDetails,
  DateTimeDetails,
  SearchContext,
  SearchContextParams,
};
